import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useToast } from "@/hooks/use-toast";
import { useWhatsAppCloudTemplateWrite } from "@/hooks/useWhatsAppCloudTemplateWrite";
import {
  type WhatsAppTemplateCategory,
  type WhatsAppTemplateWriteResult,
} from "@/lib/whatsapp-cloud-templates";
import { whatsappCloudIntegrationSelectParts } from "@/lib/whatsapp-integration-label";
import { CreateWhatsAppTemplateDialog } from "@/components/integrations/CreateWhatsAppTemplateDialog";
import {
  WhatsAppCloudTemplatesTable,
  type WhatsAppCloudTemplateRow,
} from "@/components/integrations/WhatsAppCloudTemplatesTable";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, Plus, RefreshCw } from "lucide-react";

interface CloudIntegration {
  id: string;
  status: string;
  provider: string;
  config?: {
    verified_name?: string | null;
    display_phone_number?: string | null;
    friendly_name?: string | null;
  } | null;
}

function isTemplatesWriteDisabled(result: WhatsAppTemplateWriteResult): boolean {
  if (result.ok) return false;
  const raw = result.raw as { error?: string } | undefined;
  return raw?.error === "templates_write_disabled";
}

function IntegrationSelectLabel({ integ }: { integ: CloudIntegration }) {
  const { primary, secondary } = whatsappCloudIntegrationSelectParts(integ);
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5 text-left">
      <span className="truncate font-medium leading-tight">{primary}</span>
      {secondary ? (
        <span className="truncate text-xs font-normal text-muted-foreground tabular-nums">{secondary}</span>
      ) : null}
    </span>
  );
}

export default function WhatsAppCloudTemplatesPage() {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace(user?.id);
  const { toast } = useToast();
  const { writeTemplate, loading: resubmitting } = useWhatsAppCloudTemplateWrite();
  const [searchParams] = useSearchParams();
  const integrationFromUrl = searchParams.get("integration");

  const [integrations, setIntegrations] = useState<CloudIntegration[]>([]);
  const [integrationId, setIntegrationId] = useState("");
  const [templates, setTemplates] = useState<WhatsAppCloudTemplateRow[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [resubmittingId, setResubmittingId] = useState<string | null>(null);

  const loadIntegrations = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingIntegrations(true);
    const { data, error } = await supabase
      .from("integrations")
      .select("id, status, provider, config")
      .eq("workspace_id", workspaceId)
      .eq("provider", "whatsapp_cloud")
      .eq("status", "active")
      .order("created_at");
    setLoadingIntegrations(false);
    if (error) {
      toast({ title: "Erro ao carregar integrações", description: error.message, variant: "destructive" });
      return;
    }
    const rows = (data as CloudIntegration[]) || [];
    setIntegrations(rows);
    setIntegrationId((prev) => {
      if (integrationFromUrl && rows.some((r) => r.id === integrationFromUrl)) {
        return integrationFromUrl;
      }
      return prev && rows.some((r) => r.id === prev) ? prev : rows[0]?.id || "";
    });
  }, [workspaceId, toast, integrationFromUrl]);

  const loadTemplates = useCallback(async () => {
    if (!integrationId) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    const { data, error } = await supabase
      .from("whatsapp_cloud_templates")
      .select("id, template_id, name, language, category, status, rejected_reason, last_synced_at, components")
      .eq("integration_id", integrationId)
      .order("name");
    setLoadingTemplates(false);
    if (error) {
      toast({ title: "Erro ao carregar templates", description: error.message, variant: "destructive" });
      return;
    }
    setTemplates((data as WhatsAppCloudTemplateRow[]) || []);
  }, [integrationId, toast]);

  useEffect(() => {
    void loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    void loadTemplates();
  }, [loadTemplates]);

  const handleSync = async () => {
    if (!integrationId) return;
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke("whatsapp-cloud-templates-sync", {
      body: { integration_id: integrationId },
    });
    setSyncing(false);
    if (error || (data as { error?: string })?.error) {
      toast({
        title: "Falha ao sincronizar",
        description: error?.message || (data as { error?: string })?.error || "Erro desconhecido",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Templates sincronizados",
      description: `${(data as { synced?: number })?.synced ?? 0} templates atualizados.`,
    });
    await loadTemplates();
  };

  const handleResubmit = async (row: WhatsAppCloudTemplateRow) => {
    if (!integrationId) return;
    setResubmittingId(row.id);
    const components = Array.isArray(row.components)
      ? (row.components as Record<string, unknown>[])
      : [];
    const result = await writeTemplate({
      integration_id: integrationId,
      action: "submit_for_review",
      template_id: row.template_id,
      name: row.name,
      language: row.language,
      category: (row.category || "UTILITY") as WhatsAppTemplateCategory,
      components,
      sync_after: true,
    });
    setResubmittingId(null);

    if (!result.ok) {
      toast({
        title: isTemplatesWriteDisabled(result) ? "Reenvio desabilitado" : "Falha ao reenviar",
        description: isTemplatesWriteDisabled(result)
          ? "A criação de templates está temporariamente indisponível."
          : result.error,
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Template reenviado", description: "Aguardando nova revisão da Meta." });
    await loadTemplates();
  };

  if (loadingIntegrations) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <div className="container max-w-5xl py-6 space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/integrations">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar às integrações
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Nenhuma integração WhatsApp Cloud ativa. Conecte a API oficial em Integrações.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl py-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2 w-fit" asChild>
            <Link to="/integrations">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Integrações
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">Templates WhatsApp Cloud</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie templates da Meta para mensagens fora da janela de 24 horas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handleSync} disabled={syncing || !integrationId}>
            {syncing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Sincronizar
          </Button>
          <Button onClick={() => setCreateOpen(true)} disabled={!integrationId}>
            <Plus className="mr-2 h-4 w-4" />
            Criar template
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Conta conectada</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1 max-w-md">
            <Label>Número WhatsApp</Label>
            <Select
              value={integrationId}
              onValueChange={setIntegrationId}
              disabled={integrations.length <= 1}
            >
              <SelectTrigger className="h-auto min-h-10 py-2">
                <SelectValue placeholder="Selecione o número..." />
              </SelectTrigger>
              <SelectContent>
                {integrations.map((integ) => (
                  <SelectItem key={integ.id} value={integ.id} className="py-2">
                    <IntegrationSelectLabel integ={integ} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {integrations.length <= 1 && (
              <p className="text-xs text-muted-foreground">
                Uma conta conectada neste workspace.
              </p>
            )}
          </div>

          <WhatsAppCloudTemplatesTable
            templates={templates}
            loading={loadingTemplates}
            resubmitting={resubmitting}
            resubmittingId={resubmittingId}
            onResubmit={(row) => void handleResubmit(row)}
          />
        </CardContent>
      </Card>

      <CreateWhatsAppTemplateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        integrationId={integrationId}
        onCreated={loadTemplates}
      />
    </div>
  );
}
